import cx from 'classnames'
import Styles from './BrandProfileWebsite.module.scss'
import { Input } from 'almanac-ui/TextInputField/Input'
import { useState } from 'react'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Button } from 'blaze-ui/Button'
import { ChevronRight, HighDetailedWebsite } from 'blaze-ui/icons/24'
import { ArrowRight, LinkAngled, Plus } from 'blaze-ui/icons'
import { useCreateBrandProfile } from 'hooks/queries/brandProfiles/useCreateBrandProfile'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { BrandProfileWebsiteProps } from './Types'

import { NewWebsite } from 'flows/common/steps/BrandKitForm/BrandKitMultiplePagesForm'
import { useIsUrlCrawlable } from 'hooks/useIsUrlCrawlable'
import { WebsiteListItem } from './WebsiteListItem/WebsiteListItem'
import { useCreateSource } from 'hooks/queries/workspaces/useCreateSource'
import { useCreateBrandKitSource } from 'hooks/queries/useCreateBrandKitSource'
import VirtualMarketerOnboardingStyles from 'screens/VirtualMarketerOnboarding/VirtualMarketerOnboarding.module.scss'

import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { useAuth } from 'hooks/UseAuth'
import { Text } from 'blaze-ui/Text/Text'
import { DEFAULT_LANGUAGE, LanguageInput } from 'components/Wizards/Wizard/Form/LanguageInputField/LanguageInput'
import { Heading } from 'blaze-ui/Heading/Heading'
import { BuildYourPlan } from './BuildYourPlan'
import { MediaObject } from 'types'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { BRAND_KIT_CREATED, trackEvent } from 'utils/trackingUtils'
import { useMemberProfile } from 'hooks/useMemberProfile'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { SupportChat } from 'components/FirstTimeExperienceModal/components/SupportChat/SupportChat'
import { useRouteMatch } from 'react-router-dom'
import { VIRTUAL_MARKETER_ONBOARDING_PATH } from 'paths'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { DocHighDetailed } from 'blaze-ui/icons/40'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useUpdateIterableProfile } from 'hooks/queries/useUpdateIterableProfile'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'

dayjs.extend(timezone)

const BrandProfileWebsite = ({
  onNext,
  websites: initialWebsites,
  onBack,
  brandKitId,
  language,
  onLanguageChange,
  onNoWebsite,
}: BrandProfileWebsiteProps) => {
  const [newUrl, setNewUrl] = useState<string>('') // used for single website input
  const [websites, setWebsites] = useState<NewWebsite[]>( // used for multiple website input
    initialWebsites.map((website, index) => ({ index, url: website.url, favicon: website.favicon })),
  )
  const [selectedLanguage, setSelectedLanguage] = useState<string>(language || DEFAULT_LANGUAGE)
  const [addingMore, setAddingMore] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const { isValid, errorMessage } = useIsUrlCrawlable(newUrl.trim())
  const { mutateAsync: updateIterableProfile } = useUpdateIterableProfile()
  const { mutate, isLoading: isLoadingBrandProfile } = useCreateBrandProfile()
  const { mutateAsync: createSource, isLoading: isLoadingSource } = useCreateSource()
  const auth = useAuth()
  const { mutateAsync: createBrandKitSource, isLoading: isLoadingBrandKitSource } = useCreateBrandKitSource()
  const [documents] = useState<MediaObject[]>([])
  const { trackWebsiteUrlSubmitted } = useOnboardingTracking()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { profile } = useMemberProfile(currentWorkspaceId, auth?.id)
  const isVirtualMarketerOnboardingPath = useRouteMatch(VIRTUAL_MARKETER_ONBOARDING_PATH)
  const isLoading = isLoadingSource || isLoadingBrandKitSource || isLoadingBrandProfile
  const { isMobile } = useResponsiveDesign()
  const isNoWebsiteFlowEnabled = useFlipperFlag('onboarding_no_website_flow')
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const { tierUsageStore } = useRootStore()
  const isGrowthPlan = tierUsageStore.currentSubscription?.plan === PaymentPlanKey.BlazeAutopilotGrowth

  const handleAddWebsite = () => {
    setIsDirty(true)
    if (!isValid) {
      return
    }
    setWebsites([...websites, { index: websites.length, url: newUrl.trim() }])
    setNewUrl('')
    setAddingMore(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') {
      setIsDirty(false)
      return
    }

    if (!isValid || !newUrl || websites.some((w) => w.url === newUrl)) return
    handleAddWebsite()
  }

  const onRemoveWebsite = (website: NewWebsite) => {
    const newWebsites = websites.filter((w) => w.url !== website.url)
    setWebsites(newWebsites)
  }

  const handleSubmit = async (url?: string, documents?: MediaObject[]) => {
    const submittedUrl = url ? url.trim() : websites[0]?.url
    if (submittedUrl) {
      trackWebsiteUrlSubmitted(submittedUrl)
    }

    if (brandKitId) {
      await Promise.all(
        (url ? [{ index: 0, url }] : websites).map(async (website) => {
          const source = await createSource({ url: website.url, type: 'Website' })
          return await createBrandKitSource({
            brandKitId,
            source: { type: 'Website', id: source.source.id },
          })
        }),
      )
    }

    mutate(
      {
        url: newUrl ? newUrl.trim() : websites[0].url,
        document_media_object_ids: documents?.map((doc) => doc.id) || [],
        brandKitId,
        language: selectedLanguage,
        timezone: dayjs.tz.guess(),
      },
      {
        onSuccess: (data) => {
          trackEvent(BRAND_KIT_CREATED, profile)
          updateIterableProfile({ onboarding_device: isMobile ? 'mobile' : 'desktop' })
          onNext(data.brand_profile, undefined)
        },
        onError: (error) => {
          showErrorMessageToast(error.message)
        },
      },
    )
  }

  const description = `We've added ${
    websites.length === 1 ? 'a website' : 'websites'
  } from your existing Brand Kit; edit here if needed.`

  if (isOnboardingRedesign) {
    return (
      <>
        <div className={Styles.pageContainer}>
          <div className={Styles.leftContent}>
            <div className={Styles.profileWebsiteContainer}>
              <div className={Styles.title}>
                <Heading level={1}>Enter your website to get started</Heading>
                <Text color="var(--dark-60)">Blaze scans your website to understand what makes your brand unique</Text>
              </div>
              <div className={Styles.websiteInputWrapper}>
                <Input
                  size="xl"
                  name="website"
                  type="url"
                  placeholder="Enter your website..."
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  autoFocus={!isMobile}
                  autoComplete="off"
                  autoCapitalize="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setIsDirty(true)
                      if (isValid) {
                        handleSubmit()
                      }
                    } else {
                      setIsDirty(false)
                    }
                  }}
                />
                <LanguageInput
                  onLanguageChange={(language) => {
                    setSelectedLanguage(language)
                    onLanguageChange(language)
                  }}
                  languageValue={selectedLanguage}
                  size="md"
                  wrapperClassName={Styles.languageInputWrapper}
                  popoverClassName={Styles.languageInputPopover}
                />
                <Button
                  id="brand-profile-website-next"
                  aria-label="Submit"
                  size="xl"
                  onPress={() => {
                    setIsDirty(true)
                    if (isValid) {
                      handleSubmit(newUrl, documents)
                    }
                  }}
                  isDisabled={isLoading || isDirty || !isValid || newUrl === ''}
                >
                  Build My Profile
                </Button>
              </div>
            </div>

            {isNoWebsiteFlowEnabled && (
              <div className={Styles.noWebsiteFlowContainer}>
                <Text variant="secondary" color="var(--dark-40)">
                  Don't have a website?
                </Text>
                <Button variant="tertiary" endIcon={ChevronRight} onPress={onNoWebsite}>
                  Set up manually
                </Button>
              </div>
            )}
          </div>
          <div className={Styles.blazeIllustration}>
            <img
              alt="blaze illustration"
              src="https://res.cloudinary.com/almanac/image/upload/v1766336090/blaze_assets/virtual_marketer/onboarding/Frame_1171276883_ugvrcz.png"
            />
          </div>
        </div>
      </>
    )
  }

  if (initialWebsites.length > 0) {
    return (
      <MultiStepStepLayout
        title={websites.length === 1 ? 'Confirm your website' : 'Confirm your websites'}
        description={description}
        scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
        contentClassName={VirtualMarketerOnboardingStyles.content}
        onNext={() => handleSubmit(newUrl)}
        onBack={isVirtualMarketerOnboardingPath ? undefined : onBack}
        isNextButtonDisabled={isLoading}
        nextButtonText="Continue"
      >
        <div className={cx(Styles.root, Styles.websitesRoot)}>
          <div className={Styles.tableContainer}>
            {websites.length > 0 && (
              <div className={Styles.moreWebsitesContainer}>
                {websites.map((website) => (
                  <WebsiteListItem
                    key={website.url}
                    url={website.url}
                    favicon={website.favicon}
                    onRemove={() => onRemoveWebsite(website)}
                    onUpdate={(newUrl: string) =>
                      setWebsites(websites.map((w) => (w.index === website.index ? { ...w, url: newUrl } : w)))
                    }
                  />
                ))}
              </div>
            )}

            {addingMore ? (
              <div className={Styles.addMoreContainer}>
                <LinkAngled size={24} />
                <Input
                  id="new-url"
                  name="new-url"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  errorMessage={isDirty && !isValid ? errorMessage : undefined}
                  placeholder="Enter other URL from your business website..."
                  size="lg"
                  className={Styles.newWebsiteField}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  autoCorrect="off"
                />
                <Button
                  variant="ghost-tertiary"
                  size="lg"
                  onPress={handleAddWebsite}
                  aria-label="Save"
                  isDisabled={!isValid || !newUrl}
                >
                  Add
                </Button>
              </div>
            ) : (
              <div className={Styles.addMoreButtonContainer}>
                <Button
                  variant="ghost"
                  frontIcon={Plus}
                  size="lg"
                  onPress={() => setAddingMore(true)}
                  aria-label="Add more"
                >
                  Add More
                </Button>
              </div>
            )}
          </div>

          <div className={Styles.languageInputContainer}>
            <Heading level={3}>Select Language</Heading>
            <LanguageInput
              onLanguageChange={(language) => {
                setSelectedLanguage(language)
                onLanguageChange(language)
              }}
              languageValue={selectedLanguage}
              size="xl"
              wrapperClassName={Styles.languageInputWrapper}
              popoverClassName={Styles.languageInputPopover}
            />
          </div>
          <div className={Styles.nextInputContainer}>
            <Heading level={3}>What's next?</Heading>
            <BuildYourPlan />
          </div>
        </div>
      </MultiStepStepLayout>
    )
  }

  let title: string
  if (isGrowthPlan) {
    title = `Hello, ${auth?.first_name}. Let's generate a full year of campaigns in 7 minutes.`
  } else if (isMobile) {
    title = `Welcome ${auth?.first_name}! Let's generate some content`
  } else {
    title = `👋 Welcome ${auth?.first_name}! Unlock a full week of content in just 7 minutes`
  }

  const handleOnNext = () => {
    setIsDirty(true)
    if (isValid) {
      handleSubmit(newUrl, documents)
    }
  }

  return (
    <MultiStepStepLayout
      title={title}
      description={
        brandKitId ? "We couldn't find a website from your existing Brand Kit. Enter a new business website." : ''
      }
      scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
      contentClassName={VirtualMarketerOnboardingStyles.content}
      onBack={isVirtualMarketerOnboardingPath ? undefined : onBack}
    >
      <div className={Styles.root}>
        <div className={Styles.inputWrapper}>
          <div className={Styles.box}>
            <div className={Styles.field}>
              <ResponsiveDesign.Desktop>
                <Heading level={3}>Enter your website</Heading>
              </ResponsiveDesign.Desktop>
              <ResponsiveDesign.Mobile>
                <div className={Styles.mobileHeading}>
                  <Text variant="smallList" color="var(--dark-80)">
                    Enter your website to analyze your brand and content strategy
                  </Text>
                </div>
              </ResponsiveDesign.Mobile>
              <div className={cx(Styles.inputContainer, { [Styles.invalid]: isDirty && !isValid })}>
                <ResponsiveDesign.Desktop>
                  <HighDetailedWebsite />
                </ResponsiveDesign.Desktop>
                <Input
                  size="xl"
                  name="website"
                  type="url"
                  placeholder="Enter your business website..."
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  autoFocus={!isMobile}
                  autoComplete="off"
                  autoCapitalize="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setIsDirty(true)
                      if (isValid) {
                        handleSubmit()
                      }
                    } else {
                      setIsDirty(false)
                    }
                  }}
                  withBorder={false}
                />
              </div>
            </div>
            <div className={cx(Styles.field, Styles.languageFieldContainer)}>
              <ResponsiveDesign.Desktop>
                <Text variant="smallList" color="var(--dark-80)">
                  Select Language
                </Text>
              </ResponsiveDesign.Desktop>
              <ResponsiveDesign.Mobile>
                <Text variant="smallList" color="var(--dark-80)">
                  Choose your content language
                </Text>
              </ResponsiveDesign.Mobile>
              <LanguageInput
                onLanguageChange={(language) => {
                  setSelectedLanguage(language)
                  onLanguageChange(language)
                }}
                languageValue={selectedLanguage}
                size="xl"
                wrapperClassName={Styles.languageInputWrapper}
                popoverClassName={Styles.languageInputPopover}
              />
            </div>
          </div>
          {isDirty && !isValid && <div className={Styles.errorMessage}>Please enter a valid URL</div>}
        </div>

        <ResponsiveDesign.Mobile>
          <Button
            id="brand-profile-website-next-mobile"
            aria-label="Submit"
            size="xl"
            variant="primary"
            endIcon={ArrowRight}
            onPress={handleOnNext}
            isDisabled={isLoading || isDirty || !isValid || newUrl === ''}
            fullWidth
          >
            Get Started
          </Button>
        </ResponsiveDesign.Mobile>

        {isNoWebsiteFlowEnabled && (
          <>
            <ResponsiveDesign.Desktop>
              <div className={Styles.noWebsiteCta}>
                <div className={Styles.iconAndText}>
                  <div className={Styles.icon}>
                    <DocHighDetailed size={32} />
                  </div>
                  <Text variant="placeholder" color="var(--dark-80)">
                    No website? No problem. Describe your business and upload brand files to process instead.
                  </Text>
                </div>
                <Button variant="ghost" onPress={onNoWebsite}>
                  Continue without website
                </Button>
              </div>
            </ResponsiveDesign.Desktop>
            <ResponsiveDesign.Mobile>
              <div className={Styles.noWebsiteMobileCta}>
                <Text variant="placeholder" color="var(--dark-80)">
                  No website?{' '}
                  <span className={Styles.noWebsiteMobileLink} onClick={onNoWebsite}>
                    Fill in your info manually
                  </span>
                </Text>
              </div>
            </ResponsiveDesign.Mobile>
          </>
        )}

        {/* Desktop-only additional elements */}
        <ResponsiveDesign.Desktop>
          <div className={Styles.desktopOnlyFooter}>
            <div className={Styles.rightButtons}>
              <SupportChat />
              <Button
                id="brand-profile-website-next"
                aria-label="Submit"
                size="xl"
                variant="primary"
                endIcon={ArrowRight}
                onPress={() => {
                  setIsDirty(true)
                  if (isValid) {
                    handleSubmit(newUrl, documents)
                  }
                }}
                isDisabled={isLoading || isDirty || !isValid || newUrl === ''}
              >
                Get Started
              </Button>
            </div>
          </div>
        </ResponsiveDesign.Desktop>
      </div>
    </MultiStepStepLayout>
  )
}

export { BrandProfileWebsite }
